/**
 * Starting a BRANCH (RFC 0302 S4): a child session that carries the
 * conversation it came out of, and its own harness conversation.
 *
 * `start_session` already opened a child and recorded `parentSessionId`, which
 * is what keeps it out of the top-level list. Three things were missing, and
 * all three are why a fanned-out session used to feel like a second
 * conversation to manage rather than a branch of this one:
 *
 *  - It started BLIND. The child was handed one sentence and nothing about the
 *    conversation that produced it, so the first thing it did was ask, or guess.
 *  - It started on WHATEVER HARNESS the device preferred. A Codex session would
 *    dispatch work that came back as Claude, for no reason anybody chose.
 *  - Nothing recorded where it came FROM. The parent knew it had children; the
 *    child knew nothing, so it could not show an origin line and a pane could
 *    not offer Back.
 *
 * WHY THE CONTEXT IS COPIED RATHER THAN RESUMED. Claude Code, Codex and Cursor
 * each persist one linear provider conversation and resume it by id. Two
 * branches cannot share that id — whichever ran first would be in the hidden
 * history of the second — so a branch gets a fresh provider conversation and is
 * told, in its first prompt, what it needs to know. A new Mod session id gives
 * that for free: the resume id is stored per session, and this one has none.
 *
 * WHY THE SOURCE HEADS AND NOT THE LATEST TRANSCRIPT. The branch is taken
 * against a frontier, and by the time the child's turn runs, the parent may have
 * answered something else. Rendering from the heads is what makes a branch blind
 * to its sibling, which is the whole safety property S2 built.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import { decodeLogDoc } from '../../log/codec'
import type { EntryId, RunDispatch } from '../../log/types'
import { createSession } from '../../model/session/mutations'
import { enqueueRun } from './enqueue-run'
import { renderBranchContext } from './session-context'

export interface BranchSessionResult {
  sessionId: string
  /** The frontier the branch was taken against, as recorded on the child. */
  sourceHeads: EntryId[]
  /** The harness the child was addressed to, inherited unless chosen. */
  agent?: string
}

/**
 * The harness a session is currently talking to.
 *
 * Its LATEST run, not its first: a session moved to Codex halfway through is on
 * Codex, and a branch of it should be too. `requestedModel.provider` is checked
 * first because that is what the desktop's router actually selects on; `agent`
 * is the older address and `default` means nobody chose.
 */
export function sessionAgent(log: BranchLog): string | undefined {
  let found: string | undefined
  for (const entry of log.entries()) {
    if (entry.payload.type !== 'run') continue
    const run = entry.payload.payload as {
      agent?: unknown
      requestedModel?: { provider?: unknown } | null
    }
    const provider = run.requestedModel?.provider
    if (provider != null && String(provider)) {
      found = String(provider)
      continue
    }
    const agent = run.agent != null ? String(run.agent) : ''
    if (agent && agent !== 'default') found = agent
  }
  return found
}

export async function startBranchSession(
  repo: Repo,
  opts: {
    /** The project the branch lands in — the parent's, always. */
    projectId: string
    /** The session being branched from. */
    fromSessionId: string
    title: string
    /** What the branch is being asked to do. */
    text: string
    /**
     * The frontier to branch against. Defaults to the source session's current
     * heads, which is what a turn deciding to branch right now is looking at.
     */
    heads?: readonly EntryId[]
    /** The post that caused this, for the child's origin line. */
    sourceMessageId?: string
    /** Override the inherited harness. Absent inherits the parent's (3f). */
    agent?: string
    /** World skills the first run should be given. */
    skills?: string[]
    /** RFC 0286 §5a: report back to the coordinator when this turn ends. */
    dispatch?: RunDispatch
    /** Drop the context seed. For a branch that genuinely starts from nothing. */
    carryContext?: boolean
  },
): Promise<BranchSessionResult> {
  const parent = await repo.find(opts.fromSessionId as AnyDocumentId)
  await (parent as { whenReady?: () => Promise<void> }).whenReady?.()
  const parentLog = new BranchLog(parent as never)
  const parentDoc = decodeLogDoc(parent.doc() as Record<string, unknown>)
  const sourceHeads = [...(opts.heads ?? parentLog.heads())]
  const agent = opts.agent ?? sessionAgent(parentLog)

  const child = await createSession(repo, opts.projectId, {
    title: opts.title,
    // The branching agent named it, so the one-time rename leaves it alone.
    titleSetBy: 'agent',
    parentSessionId: opts.fromSessionId,
  })
  const childId = child.documentId as string

  // BEFORE the run, so the first thing on the child's log is where it came
  // from. A reader that arrives mid-turn still finds the origin.
  new BranchLog(child as never).appendSessionBranched({
    sourceSessionId: opts.fromSessionId,
    ...(opts.sourceMessageId ? { sourceMessageId: opts.sourceMessageId } : {}),
    sourceHeads,
  })

  const seed =
    opts.carryContext === false
      ? ''
      : renderBranchContext(parentDoc, sourceHeads, {
          title: String(
            (parent.doc() as { title?: unknown })?.title ??
              'the source session',
          ),
        })
  const prompt = seed ? `${seed}\n\n${opts.text}` : opts.text

  await enqueueRun(repo, childId, prompt, {
    projectId: opts.projectId,
    ...(agent ? { agent } : {}),
    ...(opts.skills?.length ? { skills: opts.skills } : {}),
    ...(opts.dispatch ? { dispatch: opts.dispatch } : {}),
    // The child's own first turn is not a branch OF the child: it is its
    // mainline. `branch` pins context within ONE log, and this one is new.
  })

  return { sessionId: childId, sourceHeads, ...(agent ? { agent } : {}) }
}
